import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
    CourierReportCommissionRule,
    CourierReportConfiguration,
    CourierReportLateStartRule,
    CourierReportRateCard,
    CourierReportRunStatus,
} from './courier-report-settings.models';
import { CourierReportSettingsService } from './courier-report-settings.service';
import { saveAs } from 'file-saver';

@Component({
    selector: 'app-courier-report-settings',
    templateUrl: './courier-report-settings.component.html',
    styleUrls: ['./courier-report-settings.component.scss'],
})
export class CourierReportSettingsComponent implements OnInit {
    public configuration: CourierReportConfiguration = this.createEmptyConfiguration();
    public history: CourierReportConfiguration[] = [];
    public isLoading = false;
    public isSaving = false;
    public errorMessage = '';
    public successMessage = '';
    public reportRun?: CourierReportRunStatus;
    public isStartingRun = false;
    public isExporting = false;

    constructor(private _settingsService: CourierReportSettingsService) {}

    public ngOnInit(): void {
        this.load();
    }

    public addCommissionRule(): void {
        this.configuration.commissionRules.push(this.createEmptyRule());
    }

    public removeCommissionRule(index: number): void {
        this.configuration.commissionRules.splice(index, 1);
    }

    public addRateCard(): void {
        this.configuration.rateCards.push(this.createEmptyRateCard());
    }

    public removeRateCard(index: number): void {
        this.configuration.rateCards.splice(index, 1);
    }

    public addLateStartRule(): void {
        this.configuration.lateStartRules.push(this.createEmptyLateStartRule());
    }

    public removeLateStartRule(index: number): void {
        this.configuration.lateStartRules.splice(index, 1);
    }

    public save(): void {
        this.errorMessage = '';
        this.successMessage = '';

        if (!this.isValid()) {
            return;
        }

        this.isSaving = true;
        const request: CourierReportConfiguration = {
            ...this.configuration,
            validFrom: new Date(this.configuration.validFrom).toISOString(),
            commissionRules: this.configuration.commissionRules.map((rule) => ({
                ...rule,
                contractType: rule.contractType.trim(),
            })),
            rateCards: this.configuration.rateCards.map((rate) => ({
                ...rate,
                contractType: rate.contractType.trim(),
                role: rate.role.trim(),
                region: rate.region?.trim() || undefined,
            })),
            lateStartRules: this.configuration.lateStartRules.map((rule) => ({
                ...rule,
            })),
        };

        this._settingsService.createVersion(request).subscribe({
            next: (response: HttpResponse<CourierReportConfiguration>) => {
                const saved = response.body;
                if (saved) {
                    this.configuration = this.copyForNextVersion(
                        saved as CourierReportConfiguration
                    );
                }
                this.successMessage = 'Az új beállításverzió elkészült.';
                this.isSaving = false;
                this.loadHistory();
            },
            error: (error: HttpErrorResponse) => {
                this.errorMessage = this.getErrorMessage(error);
                this.isSaving = false;
            },
        });
    }

    public startDryRun(): void {
        this.startRun(true);
    }

    public startLiveRun(): void {
        if (window.confirm('A futtatás írni fogja a konfigurált riport sheeteket. Biztosan folytatod?')) {
            this.startRun(false);
        }
    }

    public exportReport(report: 'szb' | 'sum'): void {
        this.isExporting = true;
        this.errorMessage = '';
        this._settingsService.exportReport(report).subscribe({
            next: (response) => {
                const disposition = response.headers.get('content-disposition') || '';
                const match = /filename\*?=(?:UTF-8''|\")?([^\";]+)/i.exec(disposition);
                saveAs(response.body, match ? decodeURIComponent(match[1]) : `napi-riport-${report}.xlsx`);
                this.isExporting = false;
            },
            error: (error: HttpErrorResponse) => {
                this.errorMessage = this.getErrorMessage(error);
                this.isExporting = false;
            },
        });
    }

    private startRun(dryRun: boolean): void {
        this.isStartingRun = true;
        this.errorMessage = '';
        this._settingsService.startRun(dryRun).subscribe({
            next: (response) => {
                this.reportRun = response.body || undefined;
                this.isStartingRun = false;
                if (this.reportRun) this.pollRun(this.reportRun.runId);
            },
            error: (error: HttpErrorResponse) => {
                this.errorMessage = this.getErrorMessage(error);
                this.isStartingRun = false;
            },
        });
    }

    private pollRun(runId: string): void {
        window.setTimeout(() => {
            this._settingsService.getRun(runId).subscribe({
                next: (run) => {
                    this.reportRun = run;
                    if (run.state === 'Queued' || run.state === 'Running') this.pollRun(runId);
                },
                error: (error: HttpErrorResponse) =>
                    (this.errorMessage = this.getErrorMessage(error)),
            });
        }, 1000);
    }

    private load(): void {
        this.isLoading = true;
        this.errorMessage = '';
        this._settingsService.getEffective('HU').subscribe({
            next: (configuration) => {
                if (configuration) {
                    this.configuration = this.copyForNextVersion(configuration);
                }
                this.isLoading = false;
                this.loadHistory();
            },
            error: (error: HttpErrorResponse) => {
                if (error.status !== 204 && error.status !== 404) {
                    this.errorMessage = this.getErrorMessage(error);
                }
                this.isLoading = false;
                this.loadHistory();
            },
        });
    }

    private loadHistory(): void {
        this._settingsService.getHistory('HU').subscribe({
            next: (history) => (this.history = history ?? []),
            error: (error: HttpErrorResponse) => {
                this.errorMessage = this.getErrorMessage(error);
            },
        });
    }

    private isValid(): boolean {
        if (!this.configuration.changeReason.trim()) {
            this.errorMessage = 'A módosítás indoklása kötelező.';
            return false;
        }

        const contractTypes = this.configuration.commissionRules.map((rule) =>
            rule.contractType.trim().toLocaleLowerCase()
        );
        if (contractTypes.some((value) => !value)) {
            this.errorMessage = 'Minden jutalékszabályhoz kell szerződéstípus.';
            return false;
        }
        if (new Set(contractTypes).size !== contractTypes.length) {
            this.errorMessage = 'Egy szerződéstípus csak egyszer szerepelhet.';
            return false;
        }

        if (
            this.configuration.rateCards.some(
                (rate) => !rate.contractType.trim() || !rate.role.trim()
            )
        ) {
            this.errorMessage = 'Minden díjtételhez kell szerződéstípus és szerepkör.';
            return false;
        }

        const sortedLateRules = [...this.configuration.lateStartRules].sort(
            (left, right) => left.minimumMinutesExclusive - right.minimumMinutesExclusive
        );
        if (
            sortedLateRules.some(
                (rule, index) =>
                    rule.maximumMinutesInclusive <= rule.minimumMinutesExclusive ||
                    (index > 0 &&
                        rule.minimumMinutesExclusive <
                            sortedLateRules[index - 1].maximumMinutesInclusive)
            )
        ) {
            this.errorMessage = 'A késési sávok érvénytelenek vagy átfedik egymást.';
            return false;
        }
        return true;
    }

    private copyForNextVersion(
        source: CourierReportConfiguration
    ): CourierReportConfiguration {
        return {
            countryCode: source.countryCode,
            carRentalDailyFee: source.carRentalDailyFee,
            amortizationPerKm: source.amortizationPerKm,
            defaultContributionMarginPool: source.defaultContributionMarginPool,
            validFrom: this.toLocalDateTime(new Date()),
            changeReason: '',
            commissionRules: source.commissionRules.map((rule) => ({ ...rule })),
            rateCards: (source.rateCards ?? []).map((rate) => ({ ...rate })),
            lateStartRules: (source.lateStartRules ?? []).map((rule) => ({ ...rule })),
        };
    }

    private createEmptyConfiguration(): CourierReportConfiguration {
        return {
            countryCode: 'HU',
            carRentalDailyFee: 0,
            amortizationPerKm: 0,
            defaultContributionMarginPool: 0,
            validFrom: this.toLocalDateTime(new Date()),
            changeReason: '',
            commissionRules: [],
            rateCards: [],
            lateStartRules: [],
        };
    }

    private createEmptyRule(): CourierReportCommissionRule {
        return {
            contractType: '',
            commissionPercent: 0,
            isCalculationEnabled: true,
        };
    }

    private createEmptyRateCard(): CourierReportRateCard {
        return {
            contractType: '',
            role: 'Deliverer',
            region: '',
            hourlyFee: 0,
            pickingFeePerSku: 0,
            deliveryFeePerOrder: 0,
            onTimeBonusPerOrder: 0,
            complaintFreeBonusPerOrder: 0,
        };
    }

    private createEmptyLateStartRule(): CourierReportLateStartRule {
        return {
            minimumMinutesExclusive: 0,
            maximumMinutesInclusive: 0,
            deductionAmount: 0,
        };
    }

    private toLocalDateTime(date: Date): string {
        const timezoneOffset = date.getTimezoneOffset() * 60_000;
        return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
    }

    private getErrorMessage(error: HttpErrorResponse): string {
        return typeof error.error === 'string' && error.error
            ? error.error
            : 'A művelet nem sikerült. Kérlek, próbáld újra.';
    }
}
